/**
 * Manejo de dinero.
 *
 * REGLA NÚMERO UNO: la plata se guarda como ENTERO, en pesos.
 *
 * Por qué. En un computador, 0.1 + 0.2 no da 0.3, da 0.30000000000000004.
 * Los números decimales se guardan en binario y la mayoría de las fracciones
 * decimales no tienen representación exacta. En una app de finanzas eso
 * significa que sumar mil movimientos te deja un saldo con error, y que
 * dos saldos que deberían ser iguales no lo son.
 *
 * Solución: nunca usar decimales para plata. El peso colombiano no se usa
 * en fracciones en la vida diaria, así que guardamos pesos enteros y punto.
 *
 *   $ 250.000,00  →  250000   (un entero)
 *
 * Los decimales solo aparecen cuando se muestra en pantalla.
 *
 * Nota sobre el 4x1000: da fracciones de peso (el 0,4% de $1.000 es $4, pero
 * el de $1.500 es $6). Los bancos redondean. Aquí redondeamos al entero más
 * cercano y lo dejamos documentado en calcularGmf().
 */

/**
 * ── POR QUÉ PESOS ENTEROS Y NO CENTAVOS ─────────────────────────────────────
 *
 * El documento de diseño (investigacion/04-diseno-ui-ux.md §3.5) propone
 * guardar CENTAVOS. Aquí se decidió lo contrario, a propósito:
 *
 *   1. El peso colombiano no se fracciona en la vida real. Nadie paga 50
 *      centavos, y los extractos bancarios vienen en pesos enteros.
 *   2. La hoja de Google tiene que ser legible por un humano. Ver 25000000
 *      donde dice $250.000 confunde, y el requisito de legibilidad es duro.
 *   3. El argumento a favor de centavos era no perder precisión al dividir
 *      (cuotas, promedios). Eso ya está resuelto por repartir(), que reparte
 *      los pesos sobrantes en vez de redondear cada parte.
 *   4. Multiplicar todo por 100 sin necesidad invita a un bug de ×100, que en
 *      una app de plata es de los caros.
 *
 * Si algún día hay que manejar dólares o cripto, esa moneda va en su propia
 * cuenta con su propia escala, no cambiando la escala de todo.
 */

/** Espacio duro U+00A0. Intl lo usa entre el símbolo y la cifra: se RESPETA. */
const ESPACIO_DURO = ' ';

/**
 * Signo menos tipográfico U+2212, no el guion "-".
 * El guion es más corto y desalinea la columna de montos en una lista.
 */
const MENOS = '−';

const FORMATO_ENTERO = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 });
const FORMATO_UN_DECIMAL = new Intl.NumberFormat('es-CO', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/**
 * Formatea para mostrar: 250000 → "$ 250.000"
 *
 * El espacio entre "$" y la cifra es DURO (U+00A0), a propósito: evita que el
 * símbolo quede huérfano al final de un renglón. No lo cambies por un espacio
 * normal, y no hagas split(' ') sobre el resultado — fallaría en silencio.
 *
 * @param {number} pesos
 * @param {{signo?: 'auto'|'siempre', simbolo?: boolean}} opciones
 */
export function formatear(pesos, { signo = 'auto', simbolo = true } = {}) {
  if (pesos === null || pesos === undefined || Number.isNaN(pesos)) return '—';
  const negativo = pesos < 0;
  const cuerpo = FORMATO_ENTERO.format(Math.abs(pesos));
  const prefijo = negativo ? MENOS : signo === 'siempre' ? '+' : '';
  return `${prefijo}${simbolo ? '$' + ESPACIO_DURO : ''}${cuerpo}`;
}

/** Sin el símbolo: 250000 → "250.000". Para poner dentro de una frase. */
export function formatearSimple(pesos) {
  if (pesos === null || pesos === undefined || Number.isNaN(pesos)) return '—';
  return FORMATO_ENTERO.format(pesos);
}

/**
 * Versión corta para tarjetas de saldo y gráficas.
 *
 * NO uses Intl con notation:'compact': está roto para es-CO.
 * 1.200.000.000 sale como "1200 M", que es inservible. Verificado.
 *
 *   1.847.300         → "$ 1,8 M"
 *   1.200.000.000     → "$ 1,2 mil M"
 *   12.345.678.900.000 → "$ 12,3 B"
 */
export function formatearCorto(pesos) {
  if (pesos === null || pesos === undefined || Number.isNaN(pesos)) return '—';
  const n = Math.abs(pesos);
  const prefijo = pesos < 0 ? MENOS : '';

  const armar = (valor, sufijo) => {
    const texto = valor >= 100 || Number.isInteger(valor)
      ? FORMATO_ENTERO.format(valor)
      : FORMATO_UN_DECIMAL.format(valor);
    return `${prefijo}$${ESPACIO_DURO}${texto}${ESPACIO_DURO}${sufijo}`;
  };

  if (n >= 1e12) return armar(n / 1e12, 'B');
  if (n >= 1e9) return armar(n / 1e9, `mil${ESPACIO_DURO}M`);
  if (n >= 1e6) return armar(n / 1e6, 'M');
  return formatear(pesos);
}

/**
 * Texto para lectores de pantalla.
 *
 * Un lector de pantalla leyendo "$ 1.234.567" puede decir cosas como
 * "dólar uno punto doscientos treinta y cuatro". Hay que darle el texto
 * explícito. Va en aria-label junto al monto visual.
 */
export function formatearAria(pesos) {
  if (pesos === null || pesos === undefined || Number.isNaN(pesos)) return 'sin dato';
  const signo = pesos < 0 ? 'menos ' : '';
  return `${signo}${FORMATO_ENTERO.format(Math.abs(pesos))} pesos`;
}

/**
 * Separa el símbolo de los dígitos SIN cortar cadenas a mano.
 * Sirve para mostrar el "$" más pequeño que la cifra, que es lo que hacen
 * las apps que se ven bien.
 */
export function formatearPartes(pesos) {
  const partes = new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).formatToParts(pesos ?? 0);

  return {
    simbolo: partes.find((p) => p.type === 'currency')?.value ?? '$',
    cuerpo: partes
      .filter((p) => ['integer', 'group', 'decimal', 'fraction'].includes(p.type))
      .map((p) => p.value)
      .join(''),
    negativo: (pesos ?? 0) < 0,
  };
}

/**
 * Convierte lo que el usuario escribe en un entero de pesos.
 *
 * Tiene que entender cómo escribe la gente de verdad, no cómo "debería":
 *
 *   "250000"        → 250000
 *   "250.000"       → 250000    (punto de miles colombiano)
 *   "$ 250.000,00"  → 250000    (coma decimal)
 *   "30 mil"        → 30000
 *   "30k"           → 30000
 *   "treinta mil"   → 30000
 *   "una luca"      → 1000      (jerga colombiana)
 *   "un palo"       → 1000000   (jerga colombiana)
 *   "1,5 millones"  → 1500000
 *
 * Devuelve null si no logra entenderlo. Nunca adivina.
 */
export function interpretar(texto) {
  if (typeof texto === 'number') return Math.round(texto);
  if (!texto) return null;

  let s = String(texto).toLowerCase().trim()
    .replace(/\$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!s) return null;

  // Ojo con el menos: formatear() escribe U+2212 (−), no el guion. Si el
  // usuario copia un monto de la pantalla y lo pega aquí, viene con ese signo.
  // Hay que aceptar los dos, y también el paréntesis contable: "(5.000)".
  const contable = /^\(.*\)$/.test(s);
  if (contable) s = s.slice(1, -1).trim();
  const negativo = contable || /^[-−]/.test(s) || /\bmenos\b/.test(s);
  s = s.replace(/^[-−]/, '').replace(/\bmenos\b/, '').trim();

  // La gente escribe "30mil" y "30k" pegado. Entre un dígito y una letra no
  // hay frontera de palabra, así que \b no los separaría. Metemos el espacio
  // nosotros: "30mil" → "30 mil".
  s = s.replace(/(\d)([a-záéíóúñ])/gi, '$1 $2');

  // Jerga y multiplicadores. El orden importa: "millones" antes que "mil".
  const MULTIPLICADORES = [
    [/\b(mm|millones?|palos?|melones?)\b/, 1_000_000],
    [/\b(k|mil|lucas?)\b/, 1_000],
  ];

  const PALABRAS = {
    cero: 0, un: 1, una: 1, uno: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5,
    seis: 6, siete: 7, ocho: 8, nueve: 9, diez: 10, once: 11, doce: 12,
    trece: 13, catorce: 14, quince: 15, dieciseis: 16, dieciséis: 16,
    diecisiete: 17, dieciocho: 18, diecinueve: 19, veinte: 20,
    veintiuno: 21, veintidos: 22, veintidós: 22, veintitres: 23,
    veintitrés: 23, veinticuatro: 24, veinticinco: 25, treinta: 30,
    cuarenta: 40, cincuenta: 50, sesenta: 60, setenta: 70, ochenta: 80,
    noventa: 90, cien: 100, ciento: 100, doscientos: 200, trescientos: 300,
    cuatrocientos: 400, quinientos: 500, seiscientos: 600, setecientos: 700,
    ochocientos: 800, novecientos: 900,
  };

  let multiplicador = 1;
  let huboMultiplicador = false;
  for (const [patron, factor] of MULTIPLICADORES) {
    if (patron.test(s)) {
      multiplicador = factor;
      huboMultiplicador = true;
      s = s.replace(patron, ' ').trim();
      break;
    }
  }

  let base = null;

  // Caso 1: quedó un número escrito con dígitos.
  const soloDigitos = s.replace(/\s/g, '');
  if (/^[\d.,]+$/.test(soloDigitos) && /\d/.test(soloDigitos)) {
    base = interpretarNumeroColombiano(soloDigitos);
  } else if (!s && huboMultiplicador) {
    // "una luca" deja la cadena vacía tras quitar el multiplicador → vale 1.
    //
    // La condición `huboMultiplicador` es lo que impide que un guión suelto
    // se interprete como un número. En las hojas de cálculo colombianas el
    // cero se escribe "$ -", y sin esta guarda ese guión quedaba convertido
    // en −1: un peso inventado en cada celda vacía. Pasó de verdad al
    // importar la hoja real.
    base = 1;
  } else {
    // Caso 2: número en palabras. Sumamos los términos que reconozcamos.
    // "treinta" → 30, "ciento cincuenta" → 150.
    const partes = s.split(/\s+|y\s+/).filter(Boolean);
    let suma = 0;
    let reconocidas = 0;
    for (const p of partes) {
      if (p === 'y') continue;
      if (p in PALABRAS) {
        suma += PALABRAS[p];
        reconocidas++;
      }
    }
    if (reconocidas > 0) base = suma;
  }

  if (base === null || Number.isNaN(base)) return null;
  const total = Math.round(base * multiplicador);
  return negativo ? -total : total;
}

/**
 * Interpreta un número escrito a la colombiana, donde el PUNTO separa miles
 * y la COMA separa decimales. Ojo: es al revés que en inglés, y confundirlo
 * convierte $1.500 en un peso con cincuenta centavos.
 */
function interpretarNumeroColombiano(s) {
  const tienePunto = s.includes('.');
  const tieneComa = s.includes(',');

  if (tienePunto && tieneComa) {
    // "1.250.000,50" → el último separador es el decimal.
    return parseFloat(s.replace(/\./g, '').replace(',', '.'));
  }
  if (tieneComa) {
    // Una coma sola es decimal: "1500,50".
    return parseFloat(s.replace(',', '.'));
  }
  if (tienePunto) {
    const partes = s.split('.');
    const ultima = partes[partes.length - 1];
    // "250.000" son doscientos cincuenta mil, no 250 con 0 decimales.
    // Regla: si el último grupo tiene exactamente 3 dígitos y hay más de un
    // grupo, es separador de miles. Si no, lo tratamos como decimal.
    if (partes.length > 1 && ultima.length === 3) {
      return parseFloat(partes.join(''));
    }
    return parseFloat(s);
  }
  return parseFloat(s);
}

/**
 * Calcula el GMF (4x1000) sobre un monto.
 *
 * Tarifa: 4 por cada 1.000 pesos = 0,4% del valor de la operación.
 * Se cobra sobre retiros, transferencias y pagos desde cuentas bancarias.
 *
 * ⚠️ PENDIENTE DE CONFIRMAR con la investigación de finanzas:
 *    - El tope mensual exento (en UVT) y el valor de la UVT de 2026.
 *    - Qué operaciones están exentas por el artículo 879 del Estatuto Tributario.
 *    - Las reglas propias de Nequi, Daviplata y Nu.
 *    Mientras tanto la app permite editar el GMF a mano en cada movimiento,
 *    que es lo honesto: es mejor que el usuario corrija a que la app invente.
 *
 * El redondeo: los bancos cobran en pesos enteros. Redondeamos al más cercano.
 */
export function calcularGmf(monto) {
  if (!monto || monto <= 0) return 0;
  return Math.round(monto * 0.004);
}

/** Suma segura: ignora null y undefined en vez de producir NaN. */
export function sumar(...valores) {
  return valores.reduce((t, v) => t + (Number(v) || 0), 0);
}

/**
 * Reparte un monto entre N partes sin perder ni un peso.
 *
 * Dividir $100.000 en 3 cuotas da $33.333,33. Si redondeas cada cuota a
 * $33.333 y las sumas, te dan $99.999: se perdió un peso. Aquí los pesos
 * sobrantes se reparten en las primeras cuotas, así la suma siempre cuadra.
 *
 * Esto importa de verdad para las compras diferidas de la tarjeta de crédito.
 */
export function repartir(monto, partes) {
  if (partes <= 0) return [];
  const base = Math.floor(Math.abs(monto) / partes);
  const sobrante = Math.abs(monto) - base * partes;
  const signo = monto < 0 ? -1 : 1;
  return Array.from({ length: partes }, (_, i) =>
    signo * (base + (i < sobrante ? 1 : 0))
  );
}

/** Porcentaje seguro: evita dividir por cero. */
export function porcentaje(parte, total) {
  if (!total) return 0;
  return (parte / total) * 100;
}
